import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import OperacoesSelector from './OperacoesSelector';
import {
  ChevronRight, ChevronLeft, Save, Plus, Trash2, AlertTriangle,
  Clock, Layers, Box, Wrench, Zap, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  numero_of: '', artigo: '', quantidade_total: '', cliente: '',
  previsao_entrada: '', data_entrega: '', observacoes: '',
  urgente: false, aguardar_items: [], categorias_especiais: [],
};

export default function ModalNovaOF({
  open,
  onOpenChange,
  // Data props
  operacoes = [],
  allCategorias = [],
  categoriasEspeciais = [],
  operadores = [],
  modelos = [],
  clientesDB = [],
  allTags = [],
  // Shared state
  loading = false,
  setLoading,
  // Callbacks
  onSuccess,  // () => void — chamado após criação com sucesso
  filtrarOperadoresPorPermissao = null,
  existeOperadorComPermissao = false,
}) {
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [step, setStep] = useState(1);
  const [lotesInt, setLotesInt] = useState([{ numero_lote: 1, quantidade: '' }]);
  const [opsInternas, setOpsInternas] = useState([]);
  const [erroNumero, setErroNumero] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [modeloId, setModeloId] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);

  const resetInternal = () => {
    setForm({ ...INITIAL_FORM });
    setStep(1);
    setLotesInt([{ numero_lote: 1, quantidade: '' }]);
    setOpsInternas([]);
    setErroNumero('');
    setModeloId('');
    setConfirmClose(false);
  };

  const hasUnsaved = () =>
    form.numero_of.trim() !== '' ||
    form.artigo.trim() !== '' ||
    opsInternas.length > 0;

  const handleTryClose = (v) => {
    if (!v && hasUnsaved()) {
      setConfirmClose(true);
    } else if (!v) {
      onOpenChange(false);
      resetInternal();
    } else {
      onOpenChange(v);
    }
  };

  const verificarNumeroOf = async (numero) => {
    if (!numero?.trim()) { setErroNumero(''); return true; }
    setVerificando(true);
    try {
      const res = await axios.get(`${API}/ofs/verificar-numero/${encodeURIComponent(numero)}`);
      if (res.data.existe) { setErroNumero(`Já existe uma OF com o número "${numero}"`); return false; }
      setErroNumero(''); return true;
    } catch { return true; } finally { setVerificando(false); }
  };

  const handleNextStep = async () => {
    if (step === 1) {
      const camposEmFalta = [];
      if (!form.numero_of) camposEmFalta.push('Número OF');
      if (!form.artigo) camposEmFalta.push('Modelo');
      if (!form.quantidade_total) camposEmFalta.push('Quantidade Total');
      if (!form.cliente) camposEmFalta.push('Marca');
      if (camposEmFalta.length > 0) {
        toast.error(`Campo(s) obrigatório(s) em falta: ${camposEmFalta.join(', ')}`, {
          style: { fontSize: '1.05rem', padding: '16px 20px' },
          duration: 5000,
        });
        return;
      }
      const ok = await verificarNumeroOf(form.numero_of);
      if (!ok) { toast.error(`Já existe uma OF com o número "${form.numero_of}"`); return; }
      if (lotesInt.length === 1 && !lotesInt[0].quantidade) {
        setLotesInt([{ numero_lote: 1, quantidade: form.quantidade_total }]);
      }
      setStep(3);
    }
  };

  const handlePrevStep = () => {
    if (step === 3) setStep(1);
    else if (step > 1) setStep(step - 1);
  };

  const handleAddLote = () => setLotesInt([...lotesInt, { numero_lote: lotesInt.length + 1, quantidade: '' }]);

  const handleRemoveLote = (idx) => {
    if (lotesInt.length > 1)
      setLotesInt(lotesInt.filter((_, i) => i !== idx).map((l, i) => ({ ...l, numero_lote: i + 1 })));
  };

  const handleLoteChange = (idx, val) => {
    const nl = [...lotesInt]; nl[idx].quantidade = val; setLotesInt(nl);
  };

  const distribuirLotes = () => {
    const qtd = parseInt(form.quantidade_total) || 0;
    const por = Math.floor(qtd / lotesInt.length);
    const resto = qtd % lotesInt.length;
    setLotesInt(lotesInt.map((l, i) => ({ ...l, quantidade: por + (i < resto ? 1 : 0) })));
  };

  const getTotalLotes = () => lotesInt.reduce((a, l) => a + (parseInt(l.quantidade) || 0), 0);

  const handleAplicarModelo = (modelo) => {
    if (opsInternas.length > 0 &&
      !window.confirm(`Já existem ${opsInternas.length} operações. Substituir com o modelo "${modelo.nome}"?`)) return;
    setOpsInternas(modelo.operacoes.map((op, i) => ({ ...op, ordem: i, id_temp: `temp-${Date.now()}-${i}` })));
    toast.success(`Modelo "${modelo.nome}" aplicado`);
  };

  const getOperacaoInfo = (id) => operacoes.find((op) => op.id === id);

  const handleSubmit = async () => {
    setLoading && setLoading(true);
    try {
      await axios.post(`${API}/ofs`, {
        numero_of: form.numero_of,
        artigo: form.artigo,
        quantidade_total: parseInt(form.quantidade_total),
        cliente: form.cliente,
        previsao_entrada: form.previsao_entrada || undefined,
        data_entrega: form.data_entrega || undefined,
        observacoes: form.observacoes || undefined,
        urgente: form.urgente || false,
        aguardar_items: form.aguardar_items || [],
        categorias_especiais: form.categorias_especiais || [],
        operacoes: opsInternas.map((op) => ({
          operacao_id: op.operacao_id,
          tempo_ajustado: op.tempo_ajustado,
          operadores_atribuidos: op.operadores_atribuidos || [],
          ordem: op.ordem,
          grupo: op.grupo,
        })),
        lotes: [],
      });
      toast.success('OF criada com sucesso!');
      onOpenChange(false);
      resetInternal();
      onSuccess && onSuccess();
    } catch (e) {
      toast.error('Erro ao criar OF'); console.error(e);
    } finally {
      setLoading && setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleTryClose}>
        <DialogContent className={step === 3 ? 'max-w-[98vw] w-[98vw] h-[95vh] flex flex-col overflow-hidden' : 'max-w-4xl max-h-[90vh] overflow-y-auto'}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading">Nova OF - Passo {step === 1 ? '1/2' : '2/2'}</DialogTitle>
            <div className="flex items-center gap-2 mt-2">
              <div className={`h-2 flex-1 rounded ${step >= 1 ? 'bg-primary' : 'bg-stone-200'}`} />
              <div className={`h-2 flex-1 rounded ${step >= 3 ? 'bg-primary' : 'bg-stone-200'}`} />
            </div>
            <div className="flex justify-between text-xs text-stone-500 mt-1">
              <span>Dados da OF</span>
              <span>Operações</span>
            </div>
          </DialogHeader>

          {/* Passo 1 — Dados */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Número OF *</label>
                  <Input
                    value={form.numero_of}
                    onChange={(e) => { setForm({ ...form, numero_of: e.target.value }); setErroNumero(''); }}
                    onBlur={() => verificarNumeroOf(form.numero_of)}
                    placeholder="ex: OF-2024-001"
                    className={erroNumero ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
                    data-testid="numero-of-input"
                  />
                  {verificando && <p className="text-xs text-stone-500 mt-1 flex items-center gap-1"><span className="animate-spin">⏳</span> A verificar...</p>}
                  {erroNumero && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {erroNumero}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Quantidade Total *</label>
                  <Input type="number" value={form.quantidade_total} onChange={(e) => setForm({ ...form, quantidade_total: e.target.value })} placeholder="ex: 1000" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Modelo *</label>
                <Input value={form.artigo} onChange={(e) => setForm({ ...form, artigo: e.target.value })} placeholder="ex: Calça Jeans" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Marca *</label>
                <select value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white">
                  <option value="">Selecione uma marca</option>
                  {clientesDB.map((c) => <option key={c.id} value={c.nome}>{c.nome}{c.codigo ? ` (${c.codigo})` : ''}</option>)}
                </select>
                {clientesDB.length === 0 && <p className="text-xs text-amber-600 mt-1">Nenhuma marca registada. <a href="/gestor/marcas" className="underline">Criar marca</a></p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Previsão de Entrada</label>
                  <Input type="date" value={form.previsao_entrada} onChange={(e) => setForm({ ...form, previsao_entrada: e.target.value })} data-testid="previsao-entrada-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Data Entrega</label>
                  <Input type="date" value={form.data_entrega} onChange={(e) => setForm({ ...form, data_entrega: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Observações</label>
                <textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} className="w-full px-3 py-2 border border-stone-300 rounded-lg" rows={2} />
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 bg-stone-50">
                <input type="checkbox" id="urgente-checkbox-nova" checked={form.urgente} onChange={(e) => setForm({ ...form, urgente: e.target.checked })} className="h-5 w-5 rounded border-stone-300 text-red-600 focus:ring-red-500" />
                <label htmlFor="urgente-checkbox-nova" className="flex items-center gap-2 cursor-pointer">
                  <Zap className="h-5 w-5 text-red-500" />
                  <span className="font-medium text-stone-700">Marcar como Urgente</span>
                </label>
              </div>

              {/* Categorias Especiais */}
              {categoriasEspeciais.length > 0 && (
                <div className="p-4 rounded-lg border border-purple-200 bg-purple-50">
                  <p className="font-medium text-stone-700 mb-2 flex items-center gap-2"><Sparkles className="h-5 w-5 text-purple-600" />Categorias Especiais</p>
                  <p className="text-xs text-stone-500 mb-3">Se seleccionar categorias especiais, esta OF só terá lotes dessas categorias (ex: PROTO, AMOSTRA).</p>
                  <div className="space-y-2">
                    {categoriasEspeciais.map((cat) => {
                      const current = form.categorias_especiais || [];
                      const isSel = current.includes(cat.id);
                      return (
                        <div key={cat.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${isSel ? 'bg-purple-100 border-2 border-purple-400' : 'bg-white border border-stone-200 hover:border-purple-300'}`}
                          onClick={() => setForm((prev) => ({ ...prev, categorias_especiais: isSel ? current.filter((id) => id !== cat.id) : [...current, cat.id] }))}
                          data-testid={`create-cat-especial-${cat.id}`}
                        >
                          <input type="checkbox" checked={isSel} onChange={() => {}} className="h-4 w-4 rounded border-purple-300 text-purple-600 pointer-events-none" />
                          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: cat.cor || '#8b5cf6' }}>
                            <Sparkles className="h-3 w-3 text-white" />
                          </div>
                          <div className="flex-1">
                            <span className="font-medium text-stone-800">{cat.nome}</span>
                            {cat.codigo && <span className="ml-2 text-xs font-mono px-1.5 py-0.5 bg-purple-200 text-purple-700 rounded">{cat.codigo}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {(form.categorias_especiais || []).length > 0 && (
                    <p className="text-xs text-purple-700 mt-2 font-medium">
                      ✓ Esta OF terá apenas lotes das categorias: {(form.categorias_especiais || []).map((id) => categoriasEspeciais.find((c) => c.id === id)?.nome).join(', ')}
                    </p>
                  )}
                </div>
              )}

              {/* Itens em falta */}
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50">
                <p className="font-medium text-stone-700 mb-3 flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" />Itens em falta (A Aguardar)</p>
                <p className="text-xs text-stone-500 mb-3">Marque os itens que estão em falta e defina a data prevista de chegada.</p>
                <div className="space-y-3">
                  {[{ tipo: 'estrutura', label: 'Estrutura', Icon: Layers }, { tipo: 'pecas', label: 'Peças', Icon: Box }, { tipo: 'acessorios', label: 'Acessórios', Icon: Wrench }].map(({ tipo, label, Icon }) => {
                    const item = form.aguardar_items?.find((i) => i.tipo === tipo) || null;
                    const isChecked = !!item;
                    return (
                      <div key={tipo} className="flex items-center gap-3">
                        <input type="checkbox" id={`aguardar-nova-${tipo}`} checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm({ ...form, aguardar_items: [...(form.aguardar_items || []), { tipo, recebido: false, data_prevista: '' }] });
                            } else {
                              setForm({ ...form, aguardar_items: (form.aguardar_items || []).filter((i) => i.tipo !== tipo) });
                            }
                          }}
                          className="h-5 w-5 rounded border-stone-300"
                        />
                        <label htmlFor={`aguardar-nova-${tipo}`} className="flex items-center gap-2 cursor-pointer flex-1">
                          <Icon className="h-5 w-5 text-amber-600" />
                          <span className="font-medium text-stone-700">{label}</span>
                        </label>
                        {isChecked && (
                          <Input type="date" value={item?.data_prevista || ''} onChange={(e) => setForm({ ...form, aguardar_items: (form.aguardar_items || []).map((i) => i.tipo === tipo ? { ...i, data_prevista: e.target.value } : i) })} className="w-40 h-8 text-sm" placeholder="Data prevista" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleNextStep} className="rounded-full" data-testid="nova-of-next-btn">
                  Próximo <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Passo 2 — Lotes (raramente usado) */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-stone-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">Quantidade Total: {form.quantidade_total}</p>
                    <p className="text-sm text-stone-600">Soma dos lotes: {getTotalLotes()}</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getTotalLotes() === parseInt(form.quantidade_total) ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                    {getTotalLotes() === parseInt(form.quantidade_total) ? '✓ Correcto' : 'Ajustar'}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2"><Layers className="h-5 w-5" />Lotes ({lotesInt.length})</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={distribuirLotes}>Distribuir Igual</Button>
                  <Button variant="outline" size="sm" onClick={handleAddLote}><Plus className="h-4 w-4 mr-1" /> Lote</Button>
                </div>
              </div>
              {lotesInt.map((lote, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                  <span className="font-medium text-stone-700 w-20">Lote {lote.numero_lote}</span>
                  <Input type="number" value={lote.quantidade} onChange={(e) => handleLoteChange(idx, e.target.value)} placeholder="Quantidade" />
                  {lotesInt.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveLote(idx)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handlePrevStep} className="rounded-full"><ChevronLeft className="mr-2 h-4 w-4" /> Voltar</Button>
                <Button onClick={handleNextStep} className="rounded-full">Próximo <ChevronRight className="ml-2 h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* Passo 3 — Operações */}
          {step === 3 && (
            <div className="flex-1 flex flex-col overflow-hidden space-y-4 mt-2">
              <div className="flex items-center gap-4 flex-shrink-0">
                <label className="text-sm font-medium text-stone-700 whitespace-nowrap">Carregar Minuta:</label>
                {modelos.length > 0 ? (
                  <select value={modeloId} onChange={(e) => { setModeloId(e.target.value); if (e.target.value) { const m = modelos.find((m) => m.id === e.target.value); if (m) handleAplicarModelo(m); } }}
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-lg bg-white text-sm" data-testid="modelo-selector">
                    <option value="">Selecione uma minuta...</option>
                    {modelos.map((m) => <option key={m.id} value={m.id}>{m.nome} ({m.operacoes?.length || 0} operações)</option>)}
                  </select>
                ) : (
                  <span className="text-sm text-stone-500 italic">Nenhuma minuta disponível</span>
                )}
              </div>

              <OperacoesSelector
                operacoes={operacoes}
                selectedOperacoes={opsInternas}
                setSelectedOperacoes={setOpsInternas}
                categorias={allCategorias}
                operadores={operadores}
                tags={allTags}
                modelos={modelos}
                onAplicarModelo={handleAplicarModelo}
                filtrarOperadoresPorPermissao={filtrarOperadoresPorPermissao}
                getOperacaoInfo={getOperacaoInfo}
              />

              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
                <p className="text-xs text-blue-700"><strong>Dica:</strong> Arraste as operações para reordenar. Os operadores associados receberão notificação quando a OF for criada.</p>
              </div>

              <div className="flex justify-between pt-4 flex-shrink-0 border-t mt-4">
                <Button variant="outline" onClick={handlePrevStep} className="rounded-full"><ChevronLeft className="mr-2 h-4 w-4" /> Voltar</Button>
                <Button onClick={handleSubmit} disabled={loading} className="rounded-full" data-testid="criar-of-btn">
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? 'A criar...' : 'Criar OF'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmação de fechar sem guardar */}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem guardar?</AlertDialogTitle>
            <AlertDialogDescription>Tem dados não guardados. Se sair agora, todas as alterações serão perdidas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmClose(false)}>Continuar a Editar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmClose(false); onOpenChange(false); resetInternal(); }} className="bg-red-500 hover:bg-red-600">
              Sair e Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
